import type { RosterChangeCell } from "@/services/roster/changeNotices";
import {
  issuedAssignmentBand,
  issuedCellStatus,
  wardIndicatorColor,
} from "@/pages/roster-view/utils/issuedCellPresentation";
import React, { useMemo } from "react";
import ViewRosterDay from "./ViewRosterDay";
import type * as type from "@vTypes";
import type {
  IssuedSnapshotAssignmentEntry,
  Roster,
} from "@vTypes/snapshot-type.ts";
import type { MyIssuedRosterTransfer } from "@/hooks/useMyIssuedRoster";
import { normalizeScheduleCellCode } from "@utils/shift-util.ts";
import { getSnapshotCellDisplayColor } from "@/utils/snapshotRosterDisplay";
import { cn } from "@utils/cn.ts";

type AssignmentBar = {
  label: string;
  color: string;
  spanDays: number;
  groupId: string;
  muted?: boolean;
};
type WardSlice = { key: string; label: string; color: string; groupId: string };

/** 특정 일자의 파견/기간배정 대상 병동 라벨/색상. 현병동은 별도 표시하지 않는다. */
function wardSliceForDay(
  transfers: MyIssuedRosterTransfer[] | undefined,
  day: number,
  daysInMonth: number,
  wardColorByGroupId: Map<string, string> | undefined
): WardSlice | undefined {
  for (const t of transfers ?? []) {
    const s = Math.max(1, Math.floor(t.period_start_day));
    const e = Math.min(daysInMonth, Math.floor(t.period_end_day));
    if (s > e || day < s || day > e) continue;

    const groupId = (t.target_group_id ?? "").trim();
    const wardName = (t.target_group_name ?? "").trim() || groupId;
    const reason =
      t.reason === "병동이동" || t.reason === "부서이동"
        ? "병동 이동"
        : t.reason;
    const label = `${wardName} · ${reason}`;
    const color =
      wardColorByGroupId?.get(groupId) ?? wardIndicatorColor(groupId);
    return { key: `transfer:${groupId}:${reason}`, label, color, groupId };
  }
  return undefined;
}

/** 월~일 행 경계 + 병동 변경 지점 기준으로 시작 셀에서만 span 바 생성 */
function transferBarForCalendarDay(
  transfers: MyIssuedRosterTransfer[] | undefined,
  day: number,
  daysInMonth: number,
  startDay: number,
  wardColorByGroupId: Map<string, string> | undefined
): AssignmentBar | undefined {
  const colIndex = (startDay + (day - 1)) % 7;
  const today = wardSliceForDay(
    transfers,
    day,
    daysInMonth,
    wardColorByGroupId
  );
  if (!today) return undefined;

  const prev =
    day > 1
      ? wardSliceForDay(transfers, day - 1, daysInMonth, wardColorByGroupId)
      : undefined;
  const chunkStart = day === 1 || colIndex === 0 || prev?.key !== today.key;
  if (!chunkStart) return undefined;

  const weekEndDay = Math.min(daysInMonth, day + (7 - colIndex) - 1);
  let spanEnd = day;
  for (let d = day + 1; d <= weekEndDay; d++) {
    const next = wardSliceForDay(transfers, d, daysInMonth, wardColorByGroupId);
    if (!next || next.key !== today.key) break;
    spanEnd = d;
  }

  return {
    label: today.label,
    color: today.color,
    groupId: today.groupId,
    spanDays: spanEnd - day + 1,
  };
}

function dateStrInAssignmentRange(
  dateStr: string,
  e: IssuedSnapshotAssignmentEntry
): boolean {
  const start = (e.start_date ?? "").slice(0, 10);
  if (start === "") return false;
  const endRaw = e.end_date;
  const end = (endRaw == null || endRaw === "" ? e.start_date : endRaw).slice(
    0,
    10
  );
  return dateStr >= start && dateStr <= end;
}

function firstAssignmentForDate(
  entries: IssuedSnapshotAssignmentEntry[] | undefined,
  dateStr: string
): IssuedSnapshotAssignmentEntry | undefined {
  if (!entries?.length) return undefined;
  for (const e of entries) {
    if (dateStrInAssignmentRange(dateStr, e)) return e;
  }
  return undefined;
}

function wardGroupIdForDay({
  day,
  daysInMonth,
  myDayGroupIds,
  sourceWardGroupId,
  transfers,
}: {
  day: number;
  daysInMonth: number;
  myDayGroupIds: readonly (string | null)[] | undefined;
  sourceWardGroupId: string | null | undefined;
  transfers: MyIssuedRosterTransfer[] | undefined;
}): string {
  const fromSchedule = (myDayGroupIds?.[day - 1] ?? "").trim();
  if (fromSchedule !== "") return fromSchedule;

  const transfer = wardSliceForDay(transfers, day, daysInMonth, undefined);
  if (transfer?.groupId != null && transfer.groupId.trim() !== "") {
    return transfer.groupId.trim();
  }

  return (sourceWardGroupId ?? "").trim();
}

function ViewRosterCalendar({
  currentScheduleDate,
  holidayDates,
  loginUser,
  rosterSheet,
  myAssignments,
  myTransfers,
  myDayGroupIds,
  sourceWardGroupId,
  selectedWardGroupId,
  wardColorByGroupId,
  compact = false,
  capture = false,
  changeCells,
  shiftNames,
  selectedDay,
  onDaySelect,
}: {
  compact?: boolean;
  capture?: boolean;
  changeCells?: ReadonlyMap<number, RosterChangeCell>;
  selectedDay?: number;
  onDaySelect?: (day: number) => void;
  shiftNames?: readonly (string | undefined)[];
  currentScheduleDate: { year: number; month: number };
  holidayDates: string[];
  loginUser: type.LoginUser | null;
  rosterSheet: Roster;
  myAssignments?: IssuedSnapshotAssignmentEntry[];
  /** GET /me `transfers`(또는 부모에서 단일 transfer 병합) */
  myTransfers?: MyIssuedRosterTransfer[];
  myDayGroupIds?: readonly (string | null)[];
  sourceWardGroupId?: string | null;
  selectedWardGroupId?: string | null;
  wardColorByGroupId?: Map<string, string>;
}) {
  /**
   *
   * Map으로 유저정보 매핑 해서 자기 스케줄표 뿌리기
   * */

  const nurseMap = useMemo(() => {
    return new Map(rosterSheet.nurses.map((n) => [String(n.nurse_id), n]));
  }, [rosterSheet.nurses]);

  const myNurseData = nurseMap.get(String(loginUser?.nurse_id));

  if (!myNurseData) {
    return (
      <div className="roster-view-calendar roster-view-calendar--empty">
        <div className="roster-view-calendar__empty-message">
          내 근무표 정보를 찾을 수 없습니다.
        </div>
      </div>
    );
  }

  const mySchedule = myNurseData.schedule;
  const shiftColors = rosterSheet.shift_colors ?? {};

  const getShiftColor = (raw: unknown) =>
    getSnapshotCellDisplayColor(raw, shiftColors);

  const normalizeShiftCode = (shiftCode: string | null | undefined) => {
    const raw = (shiftCode ?? "").trim();
    if (!raw) return "";
    if (raw.length > 1 && raw.split("").every((ch) => ch === raw[0])) {
      return raw[0];
    }
    return raw;
  };

  const coworkerList = mySchedule.map((c, i) => {
    const mainShiftCode = normalizeShiftCode(normalizeScheduleCellCode(c));
    if (["D", "E", "N"].includes(mainShiftCode)) {
      return rosterSheet.nurses.filter(
        (n) =>
          String(n.nurse_id) !== String(loginUser?.nurse_id) &&
          normalizeScheduleCellCode(n.schedule[i]) ===
            normalizeScheduleCellCode(mySchedule[i])
      );
    } else {
      return []; // DEN이 아닌 경우 빈 배열
    }
  });

  const firstDay = new Date(
    currentScheduleDate.year,
    currentScheduleDate.month - 1,
    1
  );

  const lastDay = new Date(
    currentScheduleDate.year,
    currentScheduleDate.month,
    0
  );

  const daysInMonth = lastDay.getDate();
  let startDay = firstDay.getDay(); // 한 주 시작 : 일
  startDay = (startDay + 6) % 7; // 한 주 시작 : 월

  const nextMonthDay = new Date(
    currentScheduleDate.year,
    currentScheduleDate.month,
    1
  ).getDay();

  const weeks: React.JSX.Element[][] = [];
  let currentWeek: React.JSX.Element[] = [];

  for (let i = 0; i < startDay; i++) {
    const prevDate = new Date(firstDay);
    prevDate.setDate(prevDate.getDate() - (startDay - i));
    const day = prevDate.getDate();
    const weekend = prevDate.getDay() % 7;

    currentWeek.push(
      <td
        className={`opacity-30 ${weekend === 6 ? "roster-view-calendar__sat" : weekend === 0 ? "roster-view-calendar__sun" : ""}`}
        key={`prev-${i}`}
      >
        <div className="roster-view-calendar__date">{day}</div>
      </td>
    );
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${currentScheduleDate.year}-${String(currentScheduleDate.month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const dayIndex = day - 1;
    const myShiftRaw = mySchedule[dayIndex];
    const myShift = normalizeScheduleCellCode(myShiftRaw) || null;
    const weekend = (day + startDay) % 7;
    const isHol = holidayDates.includes(dateStr);

    const rawCellBand = issuedAssignmentBand(
      mySchedule,
      dayIndex,
      sourceWardGroupId ?? "",
      7,
      startDay
    );
    const cellBand = rawCellBand
      ? {
          ...rawCellBand,
          color:
            wardColorByGroupId?.get(rawCellBand.groupId) ?? rawCellBand.color,
        }
      : null;
    const transferBar = transferBarForCalendarDay(
      myTransfers,
      day,
      daysInMonth,
      startDay,
      wardColorByGroupId
    );

    const hit = firstAssignmentForDate(myAssignments, dateStr);
    const rawG = (hit?.target_group_id ?? "").trim();
    const legacyLabel = (hit?.target_group_name ?? "").trim();
    const legacyColor =
      hit != null
        ? (wardColorByGroupId?.get(rawG) ?? wardIndicatorColor(rawG))
        : undefined;

    const hasTransferList = (myTransfers?.length ?? 0) > 0;
    const hasCellMetadata =
      myShiftRaw != null &&
      ("target_group_id" in myShiftRaw ||
        ("reason" in myShiftRaw && "group_id" in myShiftRaw));
    const assignmentBar: AssignmentBar | null =
      cellBand ??
      (hasCellMetadata
        ? null
        : (transferBar ??
          (!hasTransferList && legacyLabel !== "" && legacyColor != null
            ? {
                label: legacyLabel,
                color: legacyColor,
                groupId: rawG,
                spanDays: 1,
              }
            : null)));
    const dayWardGroupId = wardGroupIdForDay({
      day,
      daysInMonth,
      myDayGroupIds,
      sourceWardGroupId,
      transfers: myTransfers,
    });
    const isMutedByWardSelection =
      selectedWardGroupId != null && dayWardGroupId !== selectedWardGroupId;
    const visibleAssignmentBar =
      assignmentBar != null && selectedWardGroupId
        ? {
            ...assignmentBar,
            muted: assignmentBar.groupId !== selectedWardGroupId,
          }
        : assignmentBar;

    currentWeek.push(
      <td
        key={dateStr}
        className={cn(
          assignmentBar != null &&
            assignmentBar.spanDays > 1 &&
            "!overflow-visible"
        )}
      >
        <ViewRosterDay
          compact={compact}
          changeCell={changeCells?.get(day)}
          selected={selectedDay === day}
          onSelect={onDaySelect ? () => onDaySelect(day) : undefined}
          shiftName={shiftNames?.[dayIndex]}
          day={day}
          holiday={{ weekend: weekend, isHol: isHol }}
          myShift={myShift}
          displayShiftId={myShift}
          color={getShiftColor(myShiftRaw)}
          statusLabel={issuedCellStatus(myShiftRaw)}
          coworkerList={coworkerList[dayIndex]}
          serverCoworkers={
            Array.isArray(myShiftRaw?.coworkers)
              ? myShiftRaw.coworkers.filter(
                  (nurse) => nurse.nurse_id !== myNurseData.nurse_id
                )
              : undefined
          }
          muted={isMutedByWardSelection}
          assignmentBar={visibleAssignmentBar}
        />
      </td>
    );

    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  const remainingCells = 7 - currentWeek.length;

  if (remainingCells > 0) {
    {
      Array.from({ length: remainingCells }, (_, i) => {
        const weekend = (nextMonthDay + i) % 7;
        currentWeek.push(
          <td
            key={`empty-${i + 1}`}
            className={`opacity-30 ${weekend === 6 ? "roster-view-calendar__sat" : weekend === 0 ? "roster-view-calendar__sun" : ""}`}
          >
            <div className="roster-view-calendar__date">{i + 1}</div>
          </td>
        );
      });
    }
    weeks.push(currentWeek);
  }

  return (
    <table
      className={cn(
        "roster-view-calendar",
        compact && "roster-view-calendar--compact",
        capture && "roster-view-calendar--capture"
      )}
    >
      <thead>
        <tr>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub",
              compact ? "!text-[13px]" : "!text-[15px]"
            )}
          >
            월
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub",
              compact ? "!text-[13px]" : "!text-[15px]"
            )}
          >
            화
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub",
              compact ? "!text-[13px]" : "!text-[15px]"
            )}
          >
            수
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub",
              compact ? "!text-[13px]" : "!text-[15px]"
            )}
          >
            목
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub",
              compact ? "!text-[13px]" : "!text-[15px]"
            )}
          >
            금
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sat",
              compact ? "!text-[13px]" : "!text-[15px]"
            )}
          >
            토
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sun",
              compact ? "!text-[13px]" : "!text-[15px]"
            )}
          >
            일
          </th>
        </tr>
      </thead>
      <tbody className="calendar-body">
        {weeks.map((week, idx) => (
          <tr key={idx}>{week}</tr>
        ))}
      </tbody>
    </table>
  );
}

export default ViewRosterCalendar;
